/**
 * The one tour running, if any: a "Show me" tour of a change (docs/SYSTEM.md →
 * Show me tours) or a page's first-visit tour (src/features/shared/tours).
 *
 * Module state rather than React state, because the tour outlives the screen
 * that started it: System → Change starts it, then it moves from page to page,
 * and the overlay mounted once in the root layout follows along.
 */
import { useSyncExternalStore } from 'react'
import type { TourStep } from '@/core/system'

/**
 * A step: a change's TourStep, or a page tour's, whose target may also be
 * `block:<heading>`, `button:<label>` or `css:<selector>` (tour-target.ts).
 */
export interface ActiveStep extends Omit<TourStep, 'target'> {
  target: string
  /** Not on the page right now (an empty list): skipped quietly, not a dead end. */
  optional?: boolean
}

export interface ActiveTour {
  /** The change entry it belongs to — where Done goes back to — or the page tour's name. */
  slug: string
  title: string
  steps: ActiveStep[]
  index: number
  /** The address it was started from. */
  returnTo: string
  /** A page's first-visit tour: Skip tour, and Done stays on the page. */
  kind?: 'change' | 'page'
}

let active: ActiveTour | null = null
const listeners = new Set<() => void>()

function set(next: ActiveTour | null) {
  active = next
  for (const listener of listeners) listener()
}

export function startTour(tour: Omit<ActiveTour, 'index'>) {
  if (tour.steps.length > 0) set({ ...tour, index: 0 })
}

/** Moves to a step; past the last one ends the tour. */
export function goToStep(index: number) {
  if (!active) return
  if (index >= active.steps.length) set(null)
  else set({ ...active, index: Math.max(0, index) })
}

export function endTour() {
  set(null)
}

export function useActiveTour(): ActiveTour | null {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    () => active,
    () => null,
  )
}
